import { Close } from "@mui/icons-material";
import { Box, Button, IconButton, Modal, Stack, Typography } from "@mui/joy";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { isVideoSlide, slidePoster, Story } from "../stories.data";

export default function StoryViewerView({
  story,
  slideIndex,
  slideDurationMs,
  paused,
  onPrev,
  onNext,
  onClose,
  onEnroll,
  onPauseChange,
}: {
  story: Story;
  slideIndex: number;
  slideDurationMs: number;
  paused: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onEnroll: (typeRecId: string) => void;
  onPauseChange: (paused: boolean) => void;
}) {
  const slide = story.slides[slideIndex];
  const isVideo = isVideoSlide(slide);
  const backdropSrc = isVideo ? slidePoster(slide) : slide;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Прогресс видео-слайда (0..1) для полоски вверху; у картинок её ведёт CSS.
  const [videoRatio, setVideoRatio] = useState(0);

  useEffect(() => setVideoRatio(0), [slideIndex]);

  // Длинный тап ставит на паузу — синхронно останавливаем/продолжаем ролик.
  // iOS Safari пускает автоплей только у по-настоящему muted-видео, а React не
  // всегда проставляет свойство из атрибута — форсируем его на элементе.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    if (paused) video.pause();
    else void video.play().catch(() => {});
  }, [paused, slideIndex, isVideo]);

  return (
    <Modal
      open
      onClose={onClose}
      sx={{
        "& .MuiModal-backdrop": { backdropFilter: "none", backgroundColor: "#000" },
      }}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          outline: "none",
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "column",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* размытая подложка, чтобы вертикальный постер не висел на чёрных полях */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Box
            component="img"
            src={backdropSrc}
            alt=""
            aria-hidden
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "blur(28px) brightness(0.55)",
              transform: "scale(1.15)",
            }}
          />
        </Box>

        {isVideo ? (
          <Box
            component="video"
            // key на индекс — при смене слайда ролик перемонтируется и стартует заново
            key={slideIndex}
            ref={videoRef}
            src={slide}
            poster={slidePoster(slide)}
            autoPlay
            muted
            playsInline
            preload="auto"
            onCanPlay={(event) => {
              // как только данных хватает — гарантированно стартуем (iOS часто
              // игнорирует autoPlay-атрибут до первого onCanPlay)
              const video = event.currentTarget;
              video.muted = true;
              if (!paused) void video.play().catch(() => {});
            }}
            onEnded={onNext}
            onError={onNext}
            onTimeUpdate={(event) => {
              const video = event.currentTarget;
              if (video.duration) setVideoRatio(video.currentTime / video.duration);
            }}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        ) : (
          <Box
            component="img"
            src={slide}
            alt={story.title}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        )}

        {/* зоны тапа: слева — назад, справа — вперёд; удержание ставит на паузу */}
        <Box
          onClick={onPrev}
          onPointerDown={() => onPauseChange(true)}
          onPointerUp={() => onPauseChange(false)}
          onPointerCancel={() => onPauseChange(false)}
          sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "33%" }}
        />
        <Box
          onClick={onNext}
          onPointerDown={() => onPauseChange(true)}
          onPointerUp={() => onPauseChange(false)}
          onPointerCancel={() => onPauseChange(false)}
          sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "67%" }}
        />

        <Stack
          direction="row"
          gap={0.5}
          sx={{ position: "absolute", top: 0, left: 0, right: 0, p: 1, pt: "calc(8px + env(safe-area-inset-top))" }}
        >
          {story.slides.map((slideSrc, index) => (
            <Box
              key={slideSrc}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.35)",
                overflow: "hidden",
              }}
            >
              <Box
                // key завязан на индекс слайда, чтобы анимация перезапускалась с нуля
                key={`${slideIndex}`}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  backgroundColor: "#fff",
                  transformOrigin: "left",
                  width: index < slideIndex ? "100%" : index > slideIndex ? "0%" : "100%",
                  // видео-слайд ведёт полоску по своему прогрессу, картинка — по таймеру (CSS)
                  ...(index === slideIndex && isVideo && {
                    transform: `scaleX(${videoRatio})`,
                  }),
                  ...(index === slideIndex && !isVideo && {
                    "@keyframes storyProgress": { from: { transform: "scaleX(0)" }, to: { transform: "scaleX(1)" } },
                    animation: `storyProgress ${slideDurationMs}ms linear forwards`,
                    animationPlayState: paused ? "paused" : "running",
                  }),
                }}
              />
            </Box>
          ))}
        </Stack>

        <IconButton
          onClick={onClose}
          variant="plain"
          aria-label="close"
          sx={{
            position: "absolute",
            top: "calc(16px + env(safe-area-inset-top))",
            right: 8,
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <Close />
        </IconButton>

        <Stack
          gap={1}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            p: 2,
            pb: "calc(16px + env(safe-area-inset-bottom))",
            background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))",
          }}
        >
          {story.enroll && (
            <Button
              onClick={() => onEnroll(story.enroll!.typeRecId)}
              size="lg"
              sx={{
                backgroundColor: "#D9347E",
                "&:hover": { backgroundColor: "#c02c6f" },
              }}
            >
              {story.enroll.label}
            </Button>
          )}

          {story.cta && (
            <Button
              component="a"
              href={story.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              sx={{
                backgroundColor: "#D9347E",
                "&:hover": { backgroundColor: "#c02c6f" },
              }}
            >
              {story.cta.label}
            </Button>
          )}

          {story.sourceUrl && (
            <Typography
              component="a"
              href={story.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              level="body-xs"
              textAlign="center"
              sx={{ color: "rgba(255,255,255,0.7)", textDecoration: "none" }}
            >
              <FormattedMessage defaultMessage="Читать в Telegram" />
            </Typography>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}
